import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Plus, Pencil, Trash2, GripVertical, X, Check } from 'lucide-react'
import type { CampoFormulario, CampoIn } from '../types'

const TIPOS_CAMPO = [
  { value: 'text', label: 'Texto' },
  { value: 'email', label: 'Email' },
  { value: 'tel', label: 'Teléfono' },
  { value: 'number', label: 'Número' },
  { value: 'date', label: 'Fecha' },
  { value: 'select', label: 'Selección única' },
  { value: 'provincia', label: 'Provincia (Ecuador)' },
  { value: 'radio', label: 'Radio buttons' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'checkbox_terminos', label: 'Términos y Condiciones' },
  { value: 'textarea', label: 'Área de texto' },
]

interface CamposManagerProps {
  campos: CampoFormulario[]
  onCreateCampo: (data: CampoIn) => void
  onUpdateCampo: (campoId: number, data: CampoIn) => void
  onDeleteCampo: (campoId: number) => void
}

const emptyCampo: CampoIn = {
  nombre_campo: '',
  etiqueta: '',
  tipo: 'text',
  placeholder: '',
  requerido: true,
  orden: 0,
  opciones: '',
}

export function CamposManager({ campos, onCreateCampo, onUpdateCampo, onDeleteCampo }: CamposManagerProps) {
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [formData, setFormData] = useState<CampoIn>(emptyCampo)

  const needsOptions = ['select', 'radio', 'checkbox'].includes(formData.tipo)

  const startCreate = () => {
    setFormData({ ...emptyCampo, orden: campos.length + 1 })
    setEditingId(null)
    setShowForm(true)
  }

  const startEdit = (campo: CampoFormulario) => {
    setFormData({
      nombre_campo: campo.nombre_campo,
      etiqueta: campo.etiqueta,
      tipo: campo.tipo,
      placeholder: campo.placeholder,
      requerido: campo.requerido,
      orden: campo.orden,
      opciones: campo.opciones.join('\n'),
      min_length: campo.min_length,
      max_length: campo.max_length,
    })
    setEditingId(campo.id)
    setShowForm(true)
  }

  const handleSave = () => {
    if (!formData.nombre_campo || !formData.etiqueta) return
    if (editingId) {
      onUpdateCampo(editingId, formData)
    } else {
      onCreateCampo(formData)
    }
    setShowForm(false)
    setFormData(emptyCampo)
    setEditingId(null)
  }

  const handleCancel = () => {
    setShowForm(false)
    setFormData(emptyCampo)
    setEditingId(null)
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-sm">Campos del Formulario</h3>
        <Button variant="outline" size="sm" onClick={startCreate}>
          <Plus className="mr-2 h-4 w-4" /> Agregar campo
        </Button>
      </div>

      {/* Lista de campos existentes */}
      <div className="space-y-2">
        {campos.map((campo) => (
          <Card key={campo.id} className="border border-gray-300 dark:border-gray-700">
            <CardContent className="p-3 flex items-center gap-3">
              <GripVertical className="h-4 w-4 text-muted-foreground shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm">{campo.etiqueta}</span>
                  <Badge variant="secondary" className="text-[10px]">
                    {TIPOS_CAMPO.find(t => t.value === campo.tipo)?.label || campo.tipo}
                  </Badge>
                  {campo.requerido && <Badge variant="outline" className="text-[10px]">Requerido</Badge>}
                </div>
                <p className="text-xs text-muted-foreground font-mono">{campo.nombre_campo}</p>
              </div>
              <div className="flex gap-1">
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => startEdit(campo)}>
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-red-500"
                  onClick={() => onDeleteCampo(campo.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {campos.length === 0 && !showForm && (
          <p className="text-sm text-muted-foreground text-center py-4">
            No hay campos configurados. Agrega campos para el formulario de canje.
          </p>
        )}
      </div>

      {/* Formulario de creación/edición */}
      {showForm && (
        <Card className="border-2 border-teal-500/30">
          <CardContent className="p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-sm">
                {editingId ? 'Editar campo' : 'Nuevo campo'}
              </h4>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleCancel}>
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Nombre interno (snake_case)</Label>
                <Input
                  value={formData.nombre_campo}
                  onChange={(e) => setFormData(p => ({ ...p, nombre_campo: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') }))}
                  placeholder="nombre_campo"
                  className="font-mono"
                />
              </div>
              <div>
                <Label className="text-xs">Etiqueta visible</Label>
                <Input
                  value={formData.etiqueta}
                  onChange={(e) => setFormData(p => ({ ...p, etiqueta: e.target.value }))}
                  placeholder="Nombre del Campo"
                />
              </div>
              <div>
                <Label className="text-xs">Tipo</Label>
                <Select value={formData.tipo} onValueChange={(v) => setFormData(p => ({ ...p, tipo: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIPOS_CAMPO.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Placeholder</Label>
                <Input
                  value={formData.placeholder}
                  onChange={(e) => setFormData(p => ({ ...p, placeholder: e.target.value }))}
                  placeholder="Texto de ejemplo"
                />
              </div>
              <div>
                <Label className="text-xs">Orden</Label>
                <Input
                  type="number"
                  value={formData.orden}
                  onChange={(e) => setFormData(p => ({ ...p, orden: parseInt(e.target.value) || 0 }))}
                />
              </div>
              <div className="flex items-center gap-2 pt-5">
                <input
                  type="checkbox"
                  checked={formData.requerido}
                  onChange={(e) => setFormData(p => ({ ...p, requerido: e.target.checked }))}
                  id="requerido"
                  className="rounded"
                />
                <Label htmlFor="requerido" className="text-xs">Campo obligatorio</Label>
              </div>
            </div>

            {needsOptions && (
              <div>
                <Label className="text-xs">Opciones (una por línea)</Label>
                <textarea
                  className="w-full border rounded-md p-2 text-sm min-h-[80px] bg-background"
                  value={formData.opciones}
                  onChange={(e) => setFormData(p => ({ ...p, opciones: e.target.value }))}
                  placeholder="Opción 1&#10;Opción 2&#10;Opción 3"
                />
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={handleCancel}>
                Cancelar
              </Button>
              <Button size="sm" className="bg-teal-800 hover:bg-teal-900 text-white" onClick={handleSave}>
                <Check className="mr-2 h-4 w-4" />
                {editingId ? 'Guardar cambios' : 'Crear campo'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
